import { useId } from 'react';

import { Typography, Grid, useTheme, useMediaQuery, Box, SxProps } from '@mui/material';
import { format } from 'date-fns';

import { Maybe } from '@/types';
import { BlogDetailsPage, ImageAsset as TImageAsset } from '@/types/generated';
import TAGLink from '@/components/TAGLink';
import { BlogImage, IBlogDetailsPage } from '@/components/BlogDetailsPage';
import { useAppContext } from '@/context/AppContextProvider.ctx';
export type ICardDesignType = Maybe<'Standard' | 'Rounded Compact'>;
import BlogImageCondition from '@/components/BlogList/BlogImageCondition';

import {
	blogItemBoxStyles,
	blogItemContentStyles,
	getBlogItemImageStyles,
	getBlogItemImageWrapperStyles,
	blogFormattedDateStyles,
	blogCategoryStyles,
} from './BlogList.styles';

export interface IBlogListItemProps {
	blogDetails: BlogDetailsPage;
	buttonLabel: string;
	cardDesignType?: ICardDesignType;
	displayType?: string;
}

interface ITileContentProps extends IBlogListItemProps {
	title: string;
	blogItemLink: string;
	blogImage: BlogImage;
	imageAsset: TImageAsset;
	isMobileScreen: boolean;
	path: string;
}

const DefaultTileContent = (props: Readonly<ITileContentProps>) => {
	const { title, blogItemLink, blogImage, imageAsset, isMobileScreen, path, blogDetails, displayType } = props;
	const labelId = useId();

	console.log(blogDetails);
	const initialBgColor = () => {
		switch (blogDetails.category?.name) {
			case 'Aspen Dental':
				return { backgroundColor: '#184EB1', color: 'white' };
			case 'Lovet':
				return { backgroundColor: '#3D8132', color: 'white' };
			case 'ClearChoice':
				return { backgroundColor: '#F19A31', color: '#002044' };
			case 'WellNow':
				return { backgroundColor: '#CD0E2D', color: '#002044' };
			case 'Chapter':
				return { backgroundColor: '#B80467', color: '#002044' };
			default:
				return { backgroundColor: '#FFFFFF', color: 'red' }; // Default styles
		}
	};

	const { backgroundColor, color } = initialBgColor();

	const { date } = blogDetails as IBlogDetailsPage;
	const formattedDate = format(new Date(date), 'MMMM dd, yyyy');

	return (
		<>
			<TAGLink
				href={blogItemLink}
				title={title}
				linkSx={blogItemBoxStyles(displayType ?? 'default')}
				dataTestId={`blog_list_item_link_${path}`}
				ariaLabelledby={labelId}
			>
				<Box sx={getBlogItemImageWrapperStyles(displayType ?? 'default')}>
					<BlogImageCondition
						imageAsset={imageAsset}
						blogImage={blogImage}
						imageAssetStyles={getBlogItemImageStyles}
						blogImageStyles={getBlogItemImageStyles}
						dataTestId={
							imageAsset
								? `blog_list_item_img_asset_focal_point_image_${path}`
								: `blog_list_item_focal_point_image_${path}`
						}
					/>
				</Box>
				<Grid
					item
					sx={blogItemContentStyles(displayType ?? 'default') as SxProps}
					data-test-id="blog_list_item_content_wrapper"
				>
					<Typography
						sx={blogFormattedDateStyles}
						variant={isMobileScreen ? 'bodySmallSemiBold' : 'bodyMediumSemiBold'}
						color={'#A7ADB0'}
					>
						{formattedDate}
					</Typography>
					<Typography
						sx={{
							...blogCategoryStyles,
							color: color,
							backgroundColor: backgroundColor,
						}}
						variant={'overline'}
						color={'text.primary'}
					>
						{blogDetails.category?.name}
					</Typography>
					<Typography
						sx={{ fontFamily: 'Messina Modern', color: '#FFFFFF' }}
						variant={'bodyLargeBook'}
						id={labelId}
					>
						{title}
					</Typography>
				</Grid>
			</TAGLink>
		</>
	);
};

export default function BlogListItem(props: Readonly<IBlogListItemProps>) {
	const { blogDetails } = props;

	const theme = useTheme();
	const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));

	const { config } = useAppContext();

	const title = blogDetails.title ?? '';
	const path = blogDetails.path ?? '';
	const blogRootPath = config.features.blogs.root;

	const blogItemLink = `${blogRootPath}${path}/`;

	const blogImage = blogDetails.blogImage as BlogImage;
	const imageAsset = blogDetails.imageAsset as TImageAsset;

	const defaultProps = {
		...props,
		title,
		blogItemLink,
		blogImage,
		imageAsset,
		isMobileScreen,
		path,
	};

	return (
		<Grid item width={'100%'} data-test-id="blog_list_item_container">
			<DefaultTileContent {...defaultProps} />
		</Grid>
	);
}
